import { costFormatter } from '@format/currency';
import { xor } from 'lodash';
import { getVariantsSortedByPrice } from '../../products';
import { ApiResult } from '../../api';
import {
  ACTIONS,
  ACTION_TYPES,
  PRODUCT_TYPES,
  STATE,
  pathToAction,
  ALL_RECIPES,
} from './constants';

export const showNoRecipeFound = (productTypes = {}) => {
  return Object.keys(productTypes).every(
    productType =>
      productTypes[productType].availableRecipes.fresh.length <= 0 &&
      productTypes[productType].availableRecipes.dry.length <= 0,
  );
};

export const getLowestCostVariant = starterBoxVariants => {
  const variants = getVariantsSortedByPrice(starterBoxVariants);
  return variants[0];
};

export const getPlanTypeFromRecipes = (
  recipes = { fresh: [], dry: [] },
  portionSize,
) => {
  if (!!recipes.fresh.length && !!recipes.dry.length) {
    return PRODUCT_TYPES.mixed;
  }

  if (!!recipes.fresh.length && portionSize === '0.5') {
    return PRODUCT_TYPES.half_fresh;
  }
  if (!!recipes.fresh.length) {
    return PRODUCT_TYPES.fresh;
  }

  if (!!recipes.dry.length) {
    return PRODUCT_TYPES.dry;
  }

  // If there are no recipes, we continue with the default
  return '';
};

export const getRecipeCombination = (recipeCombinations, recipes) => {
  const variant = recipeCombinations.find(
    variant =>
      !xor(variant.recipes.fresh, recipes.fresh).length &&
      !xor(variant.recipes.baked, recipes.baked).length,
  );
  return variant;
};

export const getPrices = (offer, variant, pricePeriod) => {
  return {
    discount: `${offer.formattedAmount} OFF`,
    price: costFormatter(variant['price_per_' + pricePeriod]),
    discountedPrice: costFormatter(
      variant['discounted_price_per_' + pricePeriod],
    ),
  };
};

// Create product look-alike from the line item & product.
// The lineItem model has many of the same properties but isn't
// the same shape as the product, so we take the lineItem pricing,
// because a starter box line item will have different price than
// its associated product, and the relevant info from the product
// model, to create this "cartProduct" object.
//
// Note that you'll only use this object if you're editing a pet
// with a product in the cart (hence the `null` fallback).
export const getCartProduct = (product, lineItemForPet) => {
  if (!lineItemForPet) {
    return null;
  }

  if (!product) return null;
  return {
    ...product,
    tray_count: product.variants[0].tray_count,
    variants: [
      {
        sku: lineItemForPet.sku,
        recipes: lineItemForPet.recipes,
        price_per_week: lineItemForPet.price_per_week,
        discounted_price_per_week: lineItemForPet.discounted_price_per_week,
      },
    ],
  };
};

export const saveCart = async (ctx, payload) => {
  const {
    reporter,
    combinationId,
    setShowModal,
    setLocalSelection,
    cartProduct,
    petId,
    cartApi,
  } = payload.props;
  // If the same meal plan is already in cart, no need to update
  const lineItemForPet = cartProduct;
  if (combinationId === lineItemForPet?.combination_id) {
    return ApiResult.success();
  }

  const selectedCadence = payload.props?.selectedCadence;

  let result;
  result = await cartApi.upsertMealPlan(petId, {
    combination_id: combinationId,
    ...(selectedCadence ? { subscription_cadence: selectedCadence } : {}),
  });

  // TODO: Update for Extras integration
  // if (addons?.length > 0) {
  //   result = await ApiResult.flatMapAsync(result, () =>
  //     cartApi.addAddonsToCart({
  //       pet_id: petId,
  //       addons: addons.map(addon => ({
  //         sku: addon.sku,
  //         frequency: addon.frequency,
  //         quantity: addon.quantity,
  //         recurrence: addon.recurrence,
  //       })),
  //     }),
  //   );
  // }

  ApiResult.match(result, {
    success: () => setLocalSelection(null, { skipRender: true }),
    error: {
      client: error => {
        reporter.error(error.original);
        setShowModal(true);
      },
      server: () => setShowModal(true),
    },
  });

  return result;
};

export const getHighestDailyCalories = subscriptionProducts => {
  const { fresh, dry, mixed } = subscriptionProducts;
  let sortedProductsByCalories = [fresh[0], dry[0], mixed[0]].sort((a, b) => {
    return parseInt(b.kcalories_per_day) - parseInt(a.kcalories_per_day);
  });
  return sortedProductsByCalories?.[0]?.kcalories_per_day;
};

export const onCartSubmission = service => {
  if (service.machine.current !== STATE.submitCart) {
    return Promise.resolve(ApiResult.success());
  }
  return new Promise(resolve => {
    const checkSuccess = setInterval(() => {
      if (service.machine.current !== STATE.submitCart) {
        clearInterval(checkSuccess);
        resolve(service.context.submitCartResult);
      }
    }, 500);
  });
};

export const getQuizStepNumbers = () => {
  const requiredQuestionWithAllergiesOrder = {
    pupInfo: 1,
    calories: 2,
    activity: 3,
    allergies: 4,
    userInfo: 5,
  };

  return requiredQuestionWithAllergiesOrder;
};

/**
 * Accepts a pathname as a string and returns the corresponding stateKey
 * @param {string} pathname
 * @param {string} [rootPath='']
 * @returns {string}
 */
export const getKeyFromPath = (pathname, rootPath) => {
  return pathname.replace(rootPath, '').replace(/\/$/, ''); // remove rootpath, then remove any trailing '/'
};

export const getActionFromKey = (key, service) => {
  const action = pathToAction[key];

  const machine = service.machine.original ?? service.machine;
  const transitions = machine.states[service.context.stateKey].transitions;

  if (action && Array.from(transitions.keys()).includes(action({}).type))
    return action;

  for (let [transitionKey, values] of transitions) {
    if (transitionKey === ACTION_TYPES.goBack) {
      for (let value of values) {
        if (value.guards(service.context, {})) return ACTIONS.goBack;
      }
    }
  }

  return action;
};

export const goBackReducer = (ctx, { payload }) => {
  const newCtx = {
    ...ctx,
    initialFlow: false,
    pets: payload.pets ?? ctx.pets,
    pet: payload.pet ?? ctx.pet,
    pet_id: payload.pet_id ?? ctx.pet_id,
    cart: payload.cart ?? ctx.cart,
    customer: payload.customer ?? ctx.customer,
  };

  // If customer goes back a step, and we'd set to an empty pet id,
  // and the customer actually has a pet available,
  // grab the pet id from that existing pet and set it
  // as our pet_id.
  if (newCtx.pet_id === '' && newCtx.pets?.[0]?.id) {
    newCtx.pet_id = newCtx.pets?.[0]?.id;
  }
  return newCtx;
};

export const getAddonsWithPricesAvailabilty = (
  addonConstants,
  treatProducts,
  supplementProducts,
) => {
  const treatConstants = addonConstants.filter(
    addon => addon.productType === 'TR',
  );
  const supplementConstants = addonConstants.filter(
    addon => addon.productType === 'SP',
  );
  const treatsPriceAvailibility = treatConstants.map(treatConstant => {
    const product = treatProducts.find(prod => prod.sku === treatConstant.sku); // find the product with the same SKU as the constant
    const {
      price,
      discounted_price: discountedPrice,
      product_available: isAvailable,
    } = product;
    const saveText =
      parseFloat(discountedPrice) < parseFloat(price)
        ? `(Save ${Math.ceil(
            ((parseFloat(price) - parseFloat(discountedPrice)) /
              parseFloat(price)) *
              100,
          )}%)`
        : ''; // math to get percent off full price

    return {
      ...treatConstant,
      isAvailable,
      pricingInfo: {
        price: `${product.price}`,
        discountedPrice: `${product.discounted_price}`,
        saveText,
      },
    };
  });

  const supplementsPriceAvailability = supplementConstants.map(
    supplementConstant => {
      const product = supplementProducts.find(
        prod => prod.sku === supplementConstant.sku,
      ); // find the product with the same SKU as the constnat

      const {
        price,
        discounted_price: discountedPrice,
        product_available: isAvailable,
      } = product;
      const saveText =
        parseFloat(discountedPrice) < parseFloat(price)
          ? `(Save ${Math.ceil(
              ((parseFloat(price) - parseFloat(discountedPrice)) /
                parseFloat(price)) *
                100,
            )}%)` // math to get percent off full price
          : '';

      return {
        ...supplementConstant,
        isAvailable,
        pricingInfo: {
          price: `${product.price}`,
          discountedPrice: `${product.discounted_price}`,
          saveText,
        },
      };
    },
  );

  return [...treatsPriceAvailibility, ...supplementsPriceAvailability];
};

export const getRecommendedMealplanProduct = mealplanData => {
  const recommendedComboId = mealplanData.recommended_combination_id;

  const mealplanCombo =
    mealplanData.plan_combinations.find(
      ({ combination_id }) => combination_id === recommendedComboId,
    ) ?? mealplanData.plan_combinations[0];

  return mealplanCombo;
};

export const getUnavailableRecipes = availableRecipes =>
  ALL_RECIPES.filter(recipe => !availableRecipes.includes(recipe));

export const getSelectedAllergies = (selectedAllergies, allergies) => {
  return allergies?.filter(allergy =>
    selectedAllergies?.some(sel => sel.name === allergy.name),
  );
};

export const normalize = allergies =>
  allergies?.map(allergy => ({
    label: allergy.name,
    value: allergy.id,
  }));
